import React, { useState } from 'react';
import styled from '@emotion/styled';
import { CheckCircle2, ArrowRight, Info, Lock } from 'lucide-react';
import ScaleItem from './questions/ScaleItem';
import SelfAssessmentInfo from './SelfAssessmentInfo';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * SelfAssessment — Kuution itsearviointilomake as a form (#693): seven
 * statements on agreement 1–5 with "en osaa sanoa" beside the scale, and an
 * eighth only after the workshop. Pure: items, answers and state come in as
 * props; SelfAssessmentTask fetches and posts. Submitted once per wave, then
 * locked; the page shows that it is done, not the answers, because the
 * before-state is not to be revisited until the after exists.
 */
const Wrap = styled.div`display: flex; flex-direction: column; gap: 1rem;`;
const Intro = styled.p`margin: 0; color: #9aa4b2; line-height: 1.55;`;
const TitleRow = styled.div`display: flex; align-items: center; gap: 0.6rem; font-weight: 600; color: #e8e8ef;`;
const InfoBtn = styled.button`
  all: unset; cursor: pointer; display: inline-grid; place-items: center; width: 1.6rem; height: 1.6rem; border-radius: 999px;
  border: 1px solid #2f3745; color: #9aa4b2; &:hover, &:focus-visible { border-color: #8ec1f0; color: #8ec1f0; }
`;
const Submit = styled.button`
  all: unset; cursor: pointer; align-self: flex-start; display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.75rem 1.2rem; border-radius: 10px; font-weight: 600;
  background: rgba(91, 157, 217, 0.18); border: 1px solid rgba(142, 193, 240, 0.6); color: #cfe6ff;
  &:hover, &:focus-visible { background: rgba(91, 157, 217, 0.28); }
  &:disabled { cursor: default; opacity: 0.5; }
`;
const Err = styled.p`margin: 0; color: #FF8A85; font-size: 0.88rem;`;
const SubmitRow = styled.div`display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;`;
const Final = styled.span`display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; color: #7b8794; svg { flex: 0 0 auto; }`;
const Done = styled.div`display: flex; align-items: center; gap: 0.5rem; color: #6fd3c8; font-weight: 600;`;
const Count = styled.span`font-size: 0.85rem; color: #7b8794; margin-left: auto;`;

/**
 * @param {'pre'|'post'} wave
 * @param {{ id: string, text: string }[]} items
 * @param {Record<string, number|null|undefined>} answers  1..5, null = does not know, undefined = unanswered
 */
export default function SelfAssessment({ wave = 'pre', items = [], answers = {}, onChange, onSubmit, submitted = false, submittedAt = null, own = null, busy = false, error = null }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(false);
  const labels = { 1: t('me.selfAssessment.scale.1'), 2: t('me.selfAssessment.scale.2'), 3: t('me.selfAssessment.scale.3'), 4: t('me.selfAssessment.scale.4'), 5: t('me.selfAssessment.scale.5'), unknown: t('me.selfAssessment.unknown') };
  const answered = items.filter((i) => answers[i.id] !== undefined).length;
  const complete = items.length > 0 && answered === items.length;
  const title = (
    <TitleRow>
      <span>{t(wave === 'post' ? 'me.selfAssessment.postLabel' : 'me.selfAssessment.label')}</span>
      <InfoBtn type="button" aria-label={t('me.selfAssessment.info.title')} onClick={() => setInfo(true)}><Info size={14} /></InfoBtn>
      {!submitted && <Count>{t('me.selfAssessment.progress', { n: answered, of: items.length })}</Count>}
    </TitleRow>
  );

  if (submitted) {
    return (
      <Wrap>
        {title}
        <Done><CheckCircle2 size={18} /> {t('me.selfAssessment.done')}{submittedAt ? ` · ${new Date(submittedAt).toLocaleDateString('fi-FI')}` : ''}</Done>
        <Intro>{t(wave === 'post' ? 'me.selfAssessment.postDoneNote' : 'me.selfAssessment.doneNote')}</Intro>
        {own && items.map((item) => (
          <ScaleItem key={item.id} text={item.text} value={own[item.id]} labels={labels} disabled />
        ))}
        <SelfAssessmentInfo open={info} onClose={() => setInfo(false)} />
      </Wrap>
    );
  }

  return (
    <Wrap>
      {title}
      <Intro>{t(wave === 'post' ? 'me.selfAssessment.postIntro' : 'me.selfAssessment.intro')}</Intro>
      {items.map((item) => (
        <ScaleItem key={item.id} text={item.text} value={answers[item.id]} labels={labels} disabled={busy}
          onChange={(v) => onChange?.({ ...answers, [item.id]: v })} />
      ))}
      {error && <Err>{error}</Err>}
      <SubmitRow>
        <Submit type="button" disabled={busy || !complete} onClick={onSubmit}>{t('me.selfAssessment.submit')} <ArrowRight size={16} /></Submit>
        <Final><Lock size={13} /> {t('me.selfAssessment.finalNote')}</Final>
      </SubmitRow>
      <SelfAssessmentInfo open={info} onClose={() => setInfo(false)} />
    </Wrap>
  );
}
